import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import injectSheet from 'react-jss';
import cn from 'classnames';
import { Swiper as DomSwiper } from 'swiper';
// eslint-disable-next-line import/no-unresolved
import { Autoplay, Mousewheel } from 'swiper/modules';
import Button from './Button';
import theme, { sliceColourTheme } from '../../styles/theme';
import { colourThemeType } from '../../propTypes';

class Swiper extends PureComponent {
  state = {
    prevDisabled: true, // eslint-disable-line react/no-unused-state -- used in renderButton()
    nextDisabled: false, // eslint-disable-line react/no-unused-state
  };

  componentDidUpdate(prevProps) {
    if (!this.swiper) {
      return;
    }

    const {
      children: prevChildren,
      slidesOffsetBefore: prevSlidesOffsetBefore,
      slidesOffsetAfter: prevSlidesOffsetAfter,
    } = prevProps;
    const {
      children: currChildren,
      slidesOffsetBefore: currSlidesOffsetBefore,
      slidesOffsetAfter: currSlidesOffsetAfter,
    } = this.props;

    const isNotSameSlidesOffsetAfter = prevSlidesOffsetAfter !== currSlidesOffsetAfter;
    const isNotSameSlidesOffsetBefore = prevSlidesOffsetBefore !== currSlidesOffsetBefore;

    if (isNotSameSlidesOffsetAfter || isNotSameSlidesOffsetBefore) {
      this.swiper.params.slidesOffsetBefore = currSlidesOffsetBefore;
      this.swiper.params.slidesOffsetAfter = currSlidesOffsetAfter;
      this.swiper.updateSlides();
    }

    if (prevChildren && currChildren && prevChildren.length !== currChildren.length) {
      this.swiper.update();
    }
  }

  onSlideChange = direction => {
    const { onSlideChange } = this.props;

    /* eslint no-unused-expressions: ["error", { "allowTernary": true }] */
    direction === 'prev' ? this.swiper.slidePrev() : this.swiper.slideNext();

    if (onSlideChange) onSlideChange(this.swiper.realIndex);
  };

  setRef = el => {
    if (el) {
      this.el = el;
      // TODO: Why do i need to delay this, is there something causing the content to resize?
      // If i do not do this the content is out of alignment on firefox and the isEnd & isBeginning
      // props are not calculated correctly.
      // eslint-disable-next-line no-undef
      window.setTimeout(() => {
        this.initializeSwiper();
      }, 0);
    } else {
      this.destroySwiper();
    }
  };

  getSwiperEventHandlers = () => {
    const { swiperRef, onSlideChange, onProgress } = this.props;
    const self = this;
    return {
      init() {
        if (swiperRef) {
          swiperRef(this);
        }
      },
      progress() {
        const { loop } = self.props;
        self.setState({
          prevDisabled: loop ? false : this.isBeginning,
          nextDisabled: loop ? false : this.isEnd,
        });
        if (onProgress) {
          onProgress(this.swiper);
        }
      },
      slideChangeTransitionStart() {
        if (self.swiper && onSlideChange) {
          onSlideChange(self.swiper.realIndex);
        }
      },
    };
  };

  initializeSwiper() {
    const {
      autoplay, classes, initialIndex, longSwipesRatio, loop, mousewheel, slideClassName,
      slidesOffsetBefore, slidesOffsetAfter, vertical, centerInsufficientSlides,
      autoHeight, spaceBetween,
    } = this.props;

    this.swiper = new DomSwiper(this.el, {
      modules: [Autoplay, Mousewheel],
      autoplay: autoplay && { delay: autoplay, stopOnLastSlide: false },
      direction: vertical ? 'vertical' : 'horizontal',
      grabCursor: true,
      initialSlide: initialIndex,
      longSwipesRatio,
      loop,
      centerInsufficientSlides,
      mousewheel: mousewheel && { releaseOnEdges: true },
      on: this.getSwiperEventHandlers(),
      slideClass: slideClassName && slideClassName.split(' ')[0],
      slidesOffsetAfter,
      slidesOffsetBefore,
      slidesPerView: 'auto',
      speed: 600,
      wrapperClass: classes.content,
      autoHeight,
      spaceBetween,
    });
  }

  destroySwiper() {
    if (this.swiper) {
      this.swiper.destroy();
      delete this.swiper;
      const { swiperRef } = this.props;
      if (swiperRef) {
        swiperRef(null);
      }
    }
  }

  renderButton(name) {
    const {
      classes, classNames, prevNextButtons, ready, vertical,
      buttonColor, colourTheme,
    } = this.props;
    if (prevNextButtons && ready) {
      const { prevDisabled, nextDisabled } = this.state;

      if (prevDisabled && nextDisabled) {
        return null;
      }

      const isPrev = name === 'prev';
      return (
        <Button
          className={cn(classNames[name], classes[name], 'ti-cs-swiper-buttons')}
          direction={name}
          disabled={isPrev ? prevDisabled : nextDisabled}
          disabledClassName={classNames.disabledButton}
          onClick={() => this.onSlideChange(name)}
          vertical={vertical}
          buttonColor={buttonColor}
          colourTheme={colourTheme}
        />
      );
    }

    return null;
  }

  render() {
    const {
      children, classes, classNames, ready, vertical,
    } = this.props;

    return (
      <div className={cn(classNames.wrapper, classes.wrapper)}>
        {this.renderButton('prev')}
        <div
          className={cn(classNames.container, classes.container)}
          ref={ready ? this.setRef : undefined}
        >
          <div
            className={cn(
              classes.content,
              classNames.content,
              vertical && classes.verticalContent,
              !ready && classes.contentNotReady,
            )}
          >
            {children}
          </div>
        </div>
        {this.renderButton('next')}
      </div>
    );
  }
}

Swiper.propTypes = {
  autoplay: PropTypes.oneOfType([PropTypes.number, PropTypes.bool]),
  children: PropTypes.oneOfType([
    PropTypes.element,
    PropTypes.arrayOf(PropTypes.element),
  ]).isRequired,
  classes: PropTypes.objectOf(PropTypes.string).isRequired,
  classNames: PropTypes.shape({
    wrapper: PropTypes.string,
    container: PropTypes.string,
    content: PropTypes.string,
    disabledButton: PropTypes.string,
    prev: PropTypes.string,
    next: PropTypes.string,
  }),
  initialIndex: PropTypes.number,
  longSwipesRatio: PropTypes.number,
  loop: PropTypes.bool,
  mousewheel: PropTypes.bool,
  prevNextButtons: PropTypes.bool,
  ready: PropTypes.bool,
  slideClassName: PropTypes.string.isRequired,
  slidesOffsetBefore: PropTypes.number,
  slidesOffsetAfter: PropTypes.number,
  swiperRef: PropTypes.func,
  vertical: PropTypes.bool,
  onSlideChange: PropTypes.func,
  buttonColor: PropTypes.string,
  onProgress: PropTypes.func,
  centerInsufficientSlides: PropTypes.bool,
  autoHeight: PropTypes.bool,
  spaceBetween: PropTypes.number,
  colourTheme: colourThemeType,
};

Swiper.defaultProps = {
  autoplay: false,
  classNames: {},
  initialIndex: 0,
  longSwipesRatio: 0.5,
  loop: false,
  mousewheel: false,
  prevNextButtons: true,
  ready: true,
  slidesOffsetBefore: 0,
  slidesOffsetAfter: 0,
  swiperRef: undefined,
  vertical: false,
  onSlideChange: undefined,
  buttonColor: undefined,
  onProgress: undefined,
  centerInsufficientSlides: false,
  autoHeight: false,
  spaceBetween: 0,
  colourTheme: sliceColourTheme.white,
};

export default injectSheet({
  wrapper: {
    position: 'relative',
  },
  container: {
    // these styles are adapted from node_modules/swiper/dist/css/swiper.css
    position: 'relative',
    overflow: 'hidden',
    listStyle: 'none',
    padding: 0,
    width: '100%',
    height: '100%',
    // swiper had zIndex:1 here to "fix Webkit flickering" but it doesn’t seem to be required
    tapHighlightColor: theme.colors.blackOpacity0,
    userSelect: 'none',
    touchAction: ({ vertical }) => (vertical ? 'pan-x pinch-zoom' : 'pan-y pinch-zoom'),
  },
  content: {
    // also from swiper.css
    width: '100%',
    height: '100%',
    position: 'relative',
    display: 'flex',
    alignItems: 'flex-start',
    transitionProperty: 'transform',
    boxSizing: 'content-box',
    transform: 'translate3d(0px, 0, 0)',
    '& > *': {
      flexShrink: 0,
      position: 'relative',
      transitionProperty: 'transform',
    },
    '& a': {
      userDrag: 'none',
    },
  },
  verticalContent: {
    flexDirection: 'column',
  },
})(Swiper);
